import { useCallback } from 'react';
import { toast as sonnerToast } from 'sonner';

/**
 * Drop-in shim over Sonner. The original hook returned `{ toast, show }`
 * where consumers rendered `{toast}` in their JSX. Sonner renders globally
 * via a single `<Toaster />` mounted in App.jsx, so `toast` is now `null`
 * and `{toast}` in callers renders nothing. The `show(msg, type)` API
 * stays identical so the 14 existing call sites need no changes.
 *
 * Type values supported (from existing call sites):
 *   - 'success' (default)
 *   - 'error'
 *   - 'warning'
 *   - 'info'
 */
export function useToast() {
    // show(msg, type = 'success', { undo, duration, description })
    // `undo` (feedback.md 1B): renders an Undo action and holds the toast
    // for 6s — use it on archive / delete / cancel-walkthrough style
    // actions that can be reversed.
    const show = useCallback((msg, type, opts = {}) => {
        const o = {};
        if (opts.description) o.description = opts.description;
        if (typeof opts.undo === 'function') { o.action = { label: 'Undo', onClick: opts.undo }; o.duration = opts.duration || 6000; }
        else if (opts.duration) o.duration = opts.duration;
        switch (type) {
            case 'error':
                sonnerToast.error(msg, o);
                break;
            case 'warning':
                sonnerToast.warning(msg, o);
                break;
            case 'info':
                sonnerToast.info(msg, o);
                break;
            default:
                sonnerToast.success(msg, o);
        }
    }, []);
    return { toast: null, show };
}

// Re-export Sonner's toast so new code can call it directly (with promise
// states, custom actions, etc.) without going through the hook.
export { toast } from 'sonner';
